import React, { useEffect, useState } from "react";
import Topbar from "./Topbar";
import Footer from "./Footer";

const API_URL = process.env.REACT_APP_API_URL;
const GALLERY_SLOTS = 12;

/**
 * Public Services page.
 *
 * Two sections:
 *   1. Services — intentionally laid out differently from the landing
 *      page's compact row view: an editorial intro block up top, then a
 *      3-card grid below with larger icons, expanded copy, and subtle
 *      borders so it reads as a separate piece rather than a duplicate.
 *   2. Gallery — dark-background tile grid (4 rows × 3 cols = 12 slots),
 *      fed by the Cloudinary-backed admin CRUD. Empty slots render a
 *      placeholder so the grid always feels complete.
 *
 * Vertical rhythm between the two sections is deliberately tight
 * (section-padding clamp instead of the global section-gap) so they
 * read as one "services + portfolio" composition.
 */

const SERVICES = [
  {
    num: "01",
    name: "Guided Tours of Lahore",
    desc: "Immersive walking experiences through Lahore's hidden history, culture, and architecture — designed for small groups who want something more than the tourist-circuit brief.",
    icon: "bi-geo-alt",
  },
  {
    num: "02",
    name: "Art & Historical Research",
    desc: "Long-form academic and editorial research into South Asian art, architecture, and heritage studies, delivered as written reports, publication-ready chapters, or research briefs.",
    icon: "bi-journal-richtext",
  },
  {
    num: "03",
    name: "Heritage & Cultural Consultancy",
    desc: "Strategic advisory and consultancy for cultural institutions, heritage organizations, and independent practitioners — spanning site documentation, archival research, interpretive programming, and preservation planning across South Asia.",
    icon: "bi-bank",
  },
  {
    num: "04",
    name: "Film Making",
    desc: "Creative production and storytelling via documentary and short-form film — from concept and field research through shooting, editing, and post-production delivery.",
    icon: "bi-camera-reels",
  },
];

const PlaceholderTile = () => (
  <div
    style={{
      width: "100%",
      aspectRatio: "1 / 1",
      background: "rgba(255,255,255,0.04)",
      border: "1px dashed rgba(255,255,255,0.18)",
      borderRadius: 6,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "rgba(255,255,255,0.32)",
    }}
    aria-hidden="true"
  >
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  </div>
);

export default function ServicesPage() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/gallery`)
      .then((r) => r.json())
      .then((data) => setImages(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load gallery:", err))
      .finally(() => setLoading(false));
  }, []);

  const slots = Array.from(
    { length: GALLERY_SLOTS },
    (_, i) => images[i] || null,
  );

  return (
    <>
      <Topbar />

      {/* SERVICES — editorial layout with a large intro + 3-card grid.
          Deliberately distinct from the landing page's tight-row look. */}
      <section className="services-hero">
        <div className="shell">
          <p className="eyebrow">What I Offer</p>
          <h1 className="services-hero-title">
            Art, research, film,
            <br />
            <em>heritage</em>.
          </h1>
          <p className="services-hero-lede">
            An academic practice built around heritage, research, and visual
            media — available as commissions, collaborations, or consultancy.
          </p>

          <div className="services-card-grid">
            {SERVICES.map((s) => (
              <article className="service-tile" key={s.num}>
                <div className="service-tile-top">
                  <span className="service-tile-num">{s.num}</span>
                  <i className={`bi ${s.icon} service-tile-icon`} />
                </div>
                <h3 className="service-tile-name">{s.name}</h3>
                <p className="service-tile-desc">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY — dark section, 4×3 responsive grid. */}
      <section id="gallery" className="gallery-section">
        <div className="shell">
          <h2 className="section-title" style={{ marginBottom: "2.5rem" }}>
            Media <em>gallery</em>.
          </h2>

          {loading ? (
            <div className="loader-wrap">
              <div className="loader" />
            </div>
          ) : (
            <div className="gallery-grid">
              {slots.map((img, i) =>
                img ? (
                  <div key={img._id} className="gallery-tile">
                    <img src={img.url} alt="" />
                  </div>
                ) : (
                  <PlaceholderTile key={`placeholder-${i}`} />
                ),
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        /* --- Services intro --- */
        .services-hero {
          background: var(--bg);
          color: var(--ink);
          padding: clamp(4rem, 8vw, 7rem) 0 clamp(2.5rem, 5vw, 4rem);
          border-bottom: 1px solid var(--line);
        }
        .services-hero-title {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: clamp(2.5rem, 6vw, 4.5rem);
          line-height: 1.05;
          letter-spacing: -0.02em;
          margin: 0.5rem 0 1.25rem;
          color: var(--ink);
        }
        .services-hero-title em {
          color: var(--accent);
          font-style: italic;
        }
        .services-hero-lede {
          font-size: 1.05rem;
          line-height: 1.7;
          color: var(--muted);
          max-width: 85%;
          margin: 0 0 3rem;
        }

        /* --- Services 3-card grid --- */
        .services-card-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
        }
        .service-tile {
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 1.75rem 1.75rem 2rem;
          background: var(--bg);
          transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
        }
        .service-tile:hover {
          transform: translateY(-2px);
          border-color: var(--line-strong, var(--line));
          box-shadow:
            0 1px 2px rgba(10,10,10,0.04),
            0 14px 32px rgba(10,10,10,0.09);
        }
        .service-tile-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }
        .service-tile-num {
          font-family: var(--font-display);
          font-size: 1.25rem;
          color: var(--accent);
          letter-spacing: 0.02em;
        }
        .service-tile-icon {
          font-size: 1.6rem;
          color: var(--ink);
          opacity: 0.75;
        }
        .service-tile-name {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: 1.55rem;
          line-height: 1.2;
          margin: 0 0 0.75rem;
          color: var(--ink);
        }
        .service-tile-desc {
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--muted);
          margin: 0;
        }

        /* --- Gallery on black --- */
        .gallery-section {
          background: var(--ink);
          color: var(--bg);
          padding: clamp(2.5rem, 5vw, 4rem) 0 clamp(4rem, 8vw, 7rem);
          border-top: 1px solid var(--line);
        }
        .gallery-section .eyebrow { color: var(--accent); }
        .gallery-section .section-title { color: var(--bg); }
        .gallery-section .section-title em {
          color: var(--accent);
          font-style: italic;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
        }
        .gallery-tile {
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          border-radius: 6px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.04);
        }
        .gallery-tile img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 400ms ease;
        }
        .gallery-tile:hover img { transform: scale(1.03); }

        @media (max-width: 900px) {
          .services-card-grid { grid-template-columns: repeat(2, 1fr); }
          .gallery-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 560px) {
          .services-card-grid { grid-template-columns: 1fr; }
          .gallery-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </>
  );
}
